import extend from 'extend';
import { SIDEBAR_SHIPPING_METHOD_CONFIG } from '../../constants/defaultSettings';
import { store } from '../../store';
import DEFAULT_COPY from '../../constants/defaultCopy';
import { retailerSettingsService } from '../retailer-settings/retailer-settings';
import localizedCountries from '../../lib/localizedCountries';
import { getCountryStates } from '../../lib/geolocalizationUtils';
import { receiveShippingAddress, setShippingAddressValidation } from '../../actions/addresses';
import mapToFormatAPI from '../../components/ReturnMethodSidebar/lib/mapToFormatAPI';
import mapToCorrectShippingAddress from '../../components/ReturnMethodSidebar/lib/mapToCorrectShippingAddress';
import mapShippingAddress from '../../components/ReturnMethodSidebar/lib/mapShippingAddress';
import { ADDRESS_VALIDATION } from '../../constants/endpoints';

const returnMethodSidebarFunction = () => {
  const {
    page: { countryCode, languageCode, productType },
    language,
    returnsSettings: {
      [productType]: {
        sidebarShippingMethodConfig = SIDEBAR_SHIPPING_METHOD_CONFIG,
        selectedCountries,
      } = {},
    },
  } = store.getState();
  const { [languageCode]: DEFAULT_LANG_COPY = {} } = DEFAULT_COPY;
  const { allowUserOnAddressValidationFailure = false } = retailerSettingsService.getSettings();
  const node = document.createElement('div');
  document.body.appendChild(node);

  let initialValidation = true;
  let resolvePromise;
  let rejectPromise;

  const selectInputOptions = (key) => {
    if (key === 'country') {
      const countries = localizedCountries(languageCode);
      return selectedCountries && selectedCountries.length
        ? countries.filter((country) => selectedCountries.includes(country.value))
        : countries;
    }
    return getCountryStates(countryCode).map((state) => ({
      value: state.state_name,
      label: state.state_name,
    }));
  };

  const getDefaultSidebarState = () => ({
    open: false, // open/close sidebar
    isValid: true, // check if all required fields are filled in
    isValidAddress: false,
    notificationMsg: '', // invalid messages
    sidebarData: {}, // sidebar data values
  });

  /** Immutable sidebar state */
  let sidebarState = getDefaultSidebarState();

  /**
   * Replaces the state with the new one.
   * @param newState new state object to replace with
   * @param deep deep comparision and replacement (?)
   */
  const updateState = (newState, deep = true) => {
    sidebarState = extend(deep, sidebarState, newState);
    renderSidebar();
  };

  /**
   * Updates shippingAddress with a new value.
   * @param data
   */
  const setShippingAddress = (data) => {
    store.dispatch(receiveShippingAddress(data));
  };

  /** Shows notification message when the address is invalid, not found or was replaced. */
  const shownNotificationMsg = (notificationMsg) => updateState({ notificationMsg });

  /** Client validation for disabling/enabling sidebar 'DONE' btn to proceed. */
  const clientValidate = () => {
    updateState({
      isValid:
        sidebarShippingMethodConfig.filter(
          (elem) => elem.required && !sidebarState.sidebarData[elem.key],
        ).length === 0,
    });
  };

  const isSidebarOpen = () => sidebarState.open;

  /** Closes sidebar, removes any notification messages. */
  const closeSidebar = () => {
    rejectPromise();
    updateState({ open: false });
    shownNotificationMsg('');
  };

  /**
   * Opens sidebar, populates sidebarData with a shippingAddress.
   */
  const openSidebar = () => {
    return new Promise((resolve, reject) => {
      resolvePromise = resolve;
      rejectPromise = reject;
      updateState({ open: true });
      clientValidate();
    });
  };

  const resetSidebar = () => {
    sidebarState = getDefaultSidebarState();
  };

  /**
   * Onchange handler for elements.
   * @param key
   * @param event
   * @returns {Object|*}
   */
  const change = (key, event) => {
    updateState((sidebarState.sidebarData[key] = event.target.value.trim()));
    clientValidate();
    return sidebarState.sidebarData;
  };

  /**
   * Prepare data to send for shipping address validation API.
   * @returns {{address: {street_1: (string|*|string),
   * street_2: string, city, state, zip, country: string}}}
   */
  const constructPayload = () => {
    const { sidebarData } = sidebarState;
    return mapToFormatAPI(sidebarData);
  };

  /**
   * Replaces shipping address to a correct one from the API response.
   * @param addressResponse
   */
  const replaceToCorrectShippingAddress = (addressResponse) => {
    const { sidebarData } = sidebarState;
    setShippingAddress(mapToCorrectShippingAddress(sidebarData, addressResponse));
  };

  /**
   * Map shipping address (change keys)
   * @param address
   */
  const getMappedAddress = (address) => mapShippingAddress(address);

  /**
   * Sets boolean value to the isValidAddress variable.
   * @param valid
   */
  const setAddressValidationState = (valid) => {
    updateState({ isValidAddress: valid });
  };

  /**
   * Gets the state of isValidAddress.
   */
  const getAddressValidationState = () => sidebarState.isValidAddress;

  const saveAddressAndCloseSidebar = (address) => {
    replaceToCorrectShippingAddress(address);
    setAddressValidationState(true);
    resolvePromise(getAddressValidationState());
    store.dispatch(setShippingAddressValidation(true));
    closeSidebar();
  };

  /**
   * Sends request to the API,
   * if the response is 'SUCCESS' replace the shippingAddress to
   * the correct one from the API response,
   * If the response is 'FAILURE' notify user about errors.
   */
  const serverValidate = () => {
    const {
      page: { browserFingerprint },
    } = store.getState();

    fetch(ADDRESS_VALIDATION, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'narvar-tfid': browserFingerprint,
      },
      body: JSON.stringify(constructPayload()),
    })
      .then((response) => response.json())
      .then((data) => {
        const { address, status } = data;
        if (status === 'SUCCESS') {
          saveAddressAndCloseSidebar(address);
        } else {
          const messagesWithPrompt =
            language['returns.step_2.sidepanel_address_validation_error'] ||
            DEFAULT_LANG_COPY['returns.step_2.sidepanel_address_validation_error'];
          shownNotificationMsg(messagesWithPrompt);
        }
        initialValidation = false;
      })
      .catch((error) => {
        setAddressValidationState(false);
        throw new Error(`Address Validation: ${error}`);
      });
  };

  const handleSubmit = () => {
    if (countryCode !== 'US' || (!initialValidation && allowUserOnAddressValidationFailure)) {
      saveAddressAndCloseSidebar(sidebarState.sidebarData);
    } else if (sidebarState.isValid) {
      serverValidate();
    }
  };

  /**
   * Factory method for getting appropriate control by its type.
   * @param elementProps
   * @returns {*}
   */

  /** Main react render function */
  const renderSidebar = async () => {
    const module = await import(
      /* webpackChunkName: "return-method-sidebar" */ `../../components/ReturnMethodSidebar/returnMethodSidebar`
    );
    module.renderSidebar({
      closeSidebar,
      language,
      DEFAULT_LANG_COPY,
      sidebarState,
      sidebarShippingMethodConfig,
      handleSubmit,
      node,
      selectInputOptions,
      change,
    });
  };

  return {
    isSidebarOpen,
    openSidebar,
    closeSidebar,
    resetSidebar,
    setShippingAddress,
    getMappedAddress,
    getAddressValidationState,
    setAddressValidationState,
  };
};

export const returnMethodSidebar = returnMethodSidebarFunction();
